import config from "@/config";
import { wagmiConfig } from "@/provider/PrivyProvider";
import Axios, { type AxiosError, type AxiosRequestConfig } from "axios";
import { getCookie } from "typescript-cookie";
import { getChainId } from "@wagmi/core";
import type { Config } from "wagmi";
import chainSupport, { type IChainSupport } from "@/utils/convert/chainSupport";

export const axiosInstance = Axios.create({
  baseURL: config.NETWORK_CONFIG.NEW_API_BASE_URL,
});

// Add request interceptor to attach token
axiosInstance.interceptors.request.use((config) => {
  const token = getCookie("accessToken");
  // Get the current chainId for each request
  const chainId = getChainId(wagmiConfig as Config);

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (chainSupport({ chainId } as IChainSupport)) {
    config.headers["x-chain-id"] = chainId;
  }
  return config;
});

// Global event emitter for CORS errors
export const corsErrorEvent = new EventTarget();

export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
  const source = Axios.CancelToken.source();
  const promise = axiosInstance({ ...config, cancelToken: source.token })
    .then(({ data }) => data)
    .catch((error) => {
      // Check for CORS errors
      const isCorsError =
        error.message?.includes("CORS") ||
        error.name === "NetworkError" ||
        (error.message &&
          /cross-origin|CORS policy|Access-Control-Allow/i.test(
            error.message
          )) ||
        error.code === "ERR_NETWORK";

      if (isCorsError) {
        // Dispatch CORS error event
        corsErrorEvent.dispatchEvent(
          new CustomEvent("cors-error", {
            detail: {
              message:
                "Server is currently rebuilding. Please try again later.",
              error,
            },
          })
        );
      }

      // Re-throw the error for React Query to handle
      throw error;
    });

  // @ts-ignore
  promise.cancel = () => {
    source.cancel("Query was cancelled by React Query");
  };

  return promise;
};

export default customInstance;

export interface ErrorType<Error> extends AxiosError<Error> {}
